import React, { useEffect, useState } from "react";
import {
  Stack,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Divider,
  IconButton,
  Grid2,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/config/config";
import { useDispatch, useSelector } from "react-redux";
import {
  AccountCircle,
  ArrowBack,
  ArrowRight,
  Chat,
} from "@mui/icons-material";
import Layout from "../../layouts/Layout";
import CustomTabs from "../../Components/Common/CustomTabs";
import { SecondaryBtn } from "../../Components/Common/CustomBtn";
import { getAllCreators } from "../../redux/actions/user.dispatcher";
import { ErrorSnackbar } from "../../Components/Common/CustomSnackbar";

const Conversations = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userData } = useSelector((state) => state.user);
  const { allCreators } = useSelector((state) => state.user);
  const [selectedSubscribers, setSelectedSubscribers] = useState([]);
  const [type, setType] = useState("messages");
  const [conversations, setConversations] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [message, setMessage] = useState("");
  const [sendMode, setSendMode] = useState("selected");
  const [errorState, setErrorState] = useState({
    error: false,
    message: "",
  });
  const [subscribers, setSubscribers] = useState([]);
  const [creators, setCreators] = useState(allCreators);
  const [tabsData, setTabsData] = useState(
    !userData.creatorId
      ? [
          {
            id: 1,
            label: "My Chats",
            value: "messages",
          },
          {
            id: 2,
            label: "Search",
            value: "search",
          },
        ]
      : [
          {
            id: 1,
            label: "My Chats",
            value: "messages",
          },
          {
            id: 2,
            label: "Subscribers",
            value: "subscribers",
          },
          {
            id: 2,
            label: "Search",
            value: "search",
          },
        ]
  );

  const toggleSelect = (id) => {
    setSelectedSubscribers((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    setSelectedSubscribers(subscribers.map((s) => s.id));
  };

  const deselectAll = () => {
    setSelectedSubscribers([]);
  };

  const fetchSubscribers = async () => {
    try {
      const subscribers = await axiosInstance("/creator/subscribers");

      setSubscribers(subscribers.data);
    } catch (error) {
      console.error("Error fetching conversations:", error);
    }
  };

  useEffect(() => {
    const fetchConversations = async () => {
      try {
        const response = await axiosInstance.get(
          `/chat/getUserConversations/${userData.id}`
        );

        setConversations(response.data.conversations);
      } catch (err) {
        console.error("Error fetching conversations:", err);
      }
    };
    fetchConversations();
    fetchSubscribers();
    dispatch(getAllCreators());
  }, [userData.id]);

  const handleSendMessages = async () => {
    const targetIds =
      sendMode === "all" ? subscribers.map((s) => s.id) : selectedSubscribers;

    try {
      await axiosInstance.post("/chat/bulkmessage", {
        recipientIds: targetIds,
        message: message.trim(),
      });

      setOpenDialog(false);
      setMessage("");
    } catch (error) {
      console.error("Failed to send bulk message:", error);
    }
  };

  const getOtherUser = (conversation, userId) => {
    return conversation?.userOne?.id === userId
      ? conversation.userTwo
      : conversation.userOne;
  };

  return (
    <Layout>
      <ErrorSnackbar
        error={errorState.error}
        errorMsg={errorState.message}
        handleCloseError={() => setErrorState({ error: false, message: "" })}
      />
      <Stack sx={{ height: "100vh", color: "#000" }}>
        <Stack
          direction="row"
          alignItems="center"
          sx={{ borderBottom: "1px solid #e0e0e0", mb: 2, pb: 2, gap: 2 }}
        >
          <IconButton>
            <ArrowBack
              onClick={() => {
                navigate("/dashboard");
              }}
              sx={{ color: "#000" }}
            />
          </IconButton>
          <Typography variant="h5" sx={{ fontFamily: "Inter-Bold" }}>
            Conversations
          </Typography>
        </Stack>

        <CustomTabs
          data={tabsData}
          value={type}
          setValue={setType}
          fullScreen
        />

        <Box sx={{ my: 2, flex: 1, overflow: "auto", px: 2 }}>
          {type === "messages" && (
            <List
              sx={{
                bgcolor: "#fff",
                borderRadius: 2,
                boxShadow: 2,
                color: "black",
              }}
            >
              {conversations?.length > 0 ? (
                conversations.map((conversation, index) => {
                  const otherUser = getOtherUser(conversation, userData?.id);
                  return (
                    <React.Fragment key={conversation.id}>
                      <ListItem
                        component={Link}
                        to={`/chat/${otherUser.id}`}
                        state={{ userData: otherUser }}
                        sx={{
                          textDecoration: "none",
                          color: "inherit",
                          "&:hover": {
                            backgroundColor: "#FC0108",
                            color: "#fff",
                          },
                          transition: "background-color 0.3s ease",
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            src={
                              otherUser.avatar ||
                              "https://via.placeholder.com/50"
                            }
                          />
                        </ListItemAvatar>
                        <ListItemText
                          primary={`${otherUser.firstName} ${otherUser.lastName}`}
                          secondary={
                            conversation.lastMessage || "No messages yet"
                          }
                        />
                      </ListItem>
                      {index < conversations.length - 1 && <Divider />}
                    </React.Fragment>
                  );
                })
              ) : (
                <Grid2 item size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <Box
                    sx={{
                      padding: 2,
                      width: "100%",
                      height: "200px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 2,
                    }}
                  >
                    <img
                      src={require("../../assets/icons/no-results.png")}
                      style={{
                        height: "50px",
                        width: "50px",
                      }}
                      alt="no-content"
                    />
                    <Typography
                      sx={{
                        fontFamily: "Inter-SemiBold",
                        fontSize: "24px",
                        fontWeight: "600",
                        textAlign: "center",
                      }}
                    >
                      You dont have any conversations.
                    </Typography>
                  </Box>
                </Grid2>
              )}
            </List>
          )}
          {type === "subscribers" && (
            <Box>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <SecondaryBtn
                  text="Select All"
                  noIcon
                  width={"30%"}
                  onClick={selectAll}
                />
                <SecondaryBtn
                  text="Deselect All"
                  noIcon
                  width={"30%"}
                  onClick={deselectAll}
                />
                <SecondaryBtn
                  text="Send To All"
                  noIcon
                  width={"30%"}
                  disabled={subscribers.length === 0}
                  onClick={() => {
                    setSendMode("all");
                    setOpenDialog(true);
                  }}
                />
                <SecondaryBtn
                  text="Send to Selected"
                  noIcon
                  width={"30%"}
                  disabled={selectedSubscribers.length === 0}
                  onClick={() => {
                    setSendMode("selected");
                    setOpenDialog(true);
                  }}
                />
              </Stack>
              <List
                sx={{
                  bgcolor: "#fff",
                  borderRadius: 2,
                  boxShadow: 2,
                  color: "black",
                }}
              >
                {subscribers?.length > 0 ? (
                  subscribers.map((subscriber, index) => {
                    const isSelected = selectedSubscribers.includes(
                      subscriber.id
                    );
                    return (
                      <React.Fragment key={subscriber.id}>
                        <ListItem
                          component={Link}
                          to={`/chat/${subscriber.id}`}
                          state={{ userData: subscriber }}
                          sx={{
                            textDecoration: "none",
                            color: "inherit",
                            bgcolor: isSelected ? "#FFF5F5" : "transparent",
                            "&:hover": {
                              backgroundColor: isSelected
                                ? "#FFECEC"
                                : "#f9f9f9",
                            },
                            transition: "all 0.3s ease",
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar
                              src={
                                subscriber.avatar ||
                                "https://via.placeholder.com/50"
                              }
                            />
                          </ListItemAvatar>
                          <ListItemText
                            primary={`${subscriber.firstName} ${subscriber.lastName}`}
                            secondary={
                              "Click to open chat with this subscriber"
                            }
                          />
                          <Checkbox
                            edge="end"
                            onClick={(e) => {
                              e.preventDefault();
                              toggleSelect(subscriber.id);
                            }}
                            checked={isSelected}
                            color="primary"
                          />
                        </ListItem>

                        {index < subscribers.length - 1 && <Divider />}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <Grid2 item size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <Box
                      sx={{
                        padding: 2,
                        width: "100%",
                        height: "200px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                      }}
                    >
                      <img
                        src={require("../../assets/icons/no-results.png")}
                        style={{ height: "50px", width: "50px" }}
                        alt="no-content"
                      />
                      <Typography
                        sx={{
                          fontFamily: "Inter-SemiBold",
                          fontSize: "24px",
                          fontWeight: "600",
                          textAlign: "center",
                        }}
                      >
                        You don't have any subscribers.
                      </Typography>
                    </Box>
                  </Grid2>
                )}
              </List>
            </Box>
          )}

          {type === "search" && (
            <Box>
              <TextField
                fullWidth
                placeholder="Search creators by name or email..."
                sx={{ mb: 2 }}
                onChange={(e) => {
                  const q = e.target.value.toLowerCase();
                  const filtered = allCreators
                    .filter((c) => c.id !== userData.id)
                    .filter((c) =>
                      `${c.firstName} ${c.lastName} ${c.email}`
                        .toLowerCase()
                        .includes(q)
                    );
                  setCreators(
                    filtered.map((c) => ({
                      ...c,
                      expanded:
                        creators.find((x) => x.id === c.id)?.expanded || false,
                    }))
                  );
                }}
              />

              <List
                sx={{
                  bgcolor: "#fff",
                  borderRadius: 2,
                  boxShadow: 2,
                  color: "black",
                }}
              >
                {creators?.length > 0 ? (
                  creators.map((creator, index) => {
                    const isSelected = selectedSubscribers.includes(creator.id);
                    const subscriptionList =
                      creator.creatorProfile?.subscription || [];

                    const canMessage =
                      creator.isSubscribed &&
                      subscriptionList.some(
                        (pkg) =>
                          pkg.id === creator.subscribedPackageId &&
                          pkg.accessMessaging === true
                      );

                    const packagesWithAccess = subscriptionList.filter(
                      (pkg) => pkg.accessMessaging === true
                    );

                    return (
                      <React.Fragment key={creator.id}>
                        <ListItem
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "stretch",
                            bgcolor: isSelected ? "#FFF5F5" : "transparent",
                            borderBottom:
                              index < creators.length - 1
                                ? "1px solid #eee"
                                : "none",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              width: "100%",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                flex: 1,
                                gap: 2,
                              }}
                            >
                              <Avatar
                                src={
                                  creator.avatar ||
                                  "https://via.placeholder.com/50"
                                }
                              />
                              <Box>
                                <Typography>
                                  {creator.firstName} {creator.lastName}
                                </Typography>
                                <Typography variant="body2" color="gray">
                                  {creator.email}
                                </Typography>
                              </Box>
                            </Box>

                            <Stack
                              direction="row"
                              spacing={2}
                              alignItems="center"
                            >
                              {/*  */}
                              <IconButton
                                onClick={() => navigate(`/chat/${creator.id}`)}
                                sx={{
                                  border: "1px solid #FC0108",
                                  borderRadius: "50%",
                                }}
                              >
                                <AccountCircle sx={{ color: "#FC0108" }} />
                              </IconButton>

                              <IconButton
                                onClick={() => {
                                  if (canMessage) {
                                    navigate(`/chat/${creator.id}`, {
                                      state: {
                                        userData: creator,
                                      },
                                    });
                                  } else if (!creator.isSubscribed) {
                                    setErrorState({
                                      error: true,
                                      message:
                                        "You're not subscribed to this creator.",
                                    });
                                  } else {
                                    setErrorState({
                                      error: true,
                                      message:
                                        "Your current subscription does not include messaging access.",
                                    });
                                  }
                                }}
                                sx={{
                                  border: "1px solid #FC0108",
                                  borderRadius: "50%",
                                }}
                              >
                                <Chat sx={{ color: "#FC0108" }} />
                              </IconButton>

                              <IconButton
                                onClick={() => {
                                  setCreators((prev) =>
                                    prev.map((c) =>
                                      c.id === creator.id
                                        ? { ...c, expanded: !c.expanded }
                                        : c
                                    )
                                  );
                                }}
                                sx={{
                                  border: "1px solid #FC0108",
                                  borderRadius: "50%",
                                }}
                              >
                                <ArrowRight
                                  sx={{
                                    transform: creator.expanded
                                      ? "rotate(90deg)"
                                      : "rotate(0deg)",
                                    transition: "transform 0.2s ease-in-out",
                                    color: "#FC0108",
                                  }}
                                />
                              </IconButton>
                            </Stack>
                          </Box>

                          {creator.expanded &&
                            packagesWithAccess?.length > 0 && (
                              <Box sx={{ mt: 2, pl: 7 }}>
                                <Typography fontWeight={600} mb={1}>
                                  Following packages provide access to creator
                                  messages:
                                </Typography>
                                <List dense>
                                  {packagesWithAccess.map((pkg) => (
                                    <ListItem key={pkg.id}>
                                      <ListItemText
                                        primary={`${pkg.packageName} - $${pkg.price}`}
                                        secondary={pkg.description}
                                      />
                                    </ListItem>
                                  ))}
                                </List>
                              </Box>
                            )}
                        </ListItem>
                      </React.Fragment>
                    );
                  })
                ) : (
                  <Grid2 item size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <Box
                      sx={{
                        padding: 2,
                        width: "100%",
                        height: "200px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                      }}
                    >
                      <img
                        src={require("../../assets/icons/no-results.png")}
                        style={{ height: "50px", width: "50px" }}
                        alt="no-content"
                      />
                      <Typography
                        sx={{
                          fontFamily: "Inter-SemiBold",
                          fontSize: "24px",
                          fontWeight: "600",
                          textAlign: "center",
                        }}
                      >
                        No creators match your search.
                      </Typography>
                    </Box>
                  </Grid2>
                )}
              </List>
            </Box>
          )}
        </Box>
      </Stack>
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        PaperProps={{
          sx: {
            width: "600px",
            maxWidth: "90%",
          },
        }}
      >
        <DialogTitle>Send Message</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Type your message here..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSendMessages}
            disabled={!message.trim()}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default Conversations;
